import React from 'react';
import { Button, Dropdown, Modal, Space, Tag } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  PushpinFilled,
  PushpinOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Product } from '../../../types/product';
import { formatDate } from '../../../utils/dateUtils';

const duplicateStatusLabels: Record<string, string> = {
  none: '普通',
  candidate: '候选',
  confirmed: '已确认',
  ignored: '已忽略',
};

const duplicateStatusColors: Record<string, string> = {
  none: 'default',
  candidate: 'orange',
  confirmed: 'red',
  ignored: 'blue',
};

interface ProductColumnHandlers {
  onEdit: (product: Product) => void;
  onDelete: (id: number) => void;
  onTogglePin: (id: number, isPinned: boolean) => void;
  onToggleStatus: (id: number, status: '在售' | '已下架') => void;
  onShowDetail: (product: Product) => void;
  onFetchPlatform172Detail: (product: Product) => void;
  onFetchExternalDetail: (id: number) => void;
}

export const createProductColumns = ({
  onEdit,
  onDelete,
  onTogglePin,
  onToggleStatus,
  onShowDetail,
  onFetchPlatform172Detail,
  onFetchExternalDetail,
}: ProductColumnHandlers): ColumnsType<Product> => [
  {
    title: '置顶',
    dataIndex: 'is_pinned',
    width: 60,
    render: (isPinned, record) => (
      <Button
        type="text"
        icon={isPinned ? <PushpinFilled /> : <PushpinOutlined />}
        onClick={() => onTogglePin(record.id, isPinned)}
        style={{ color: isPinned ? '#1677ff' : 'inherit' }}
      />
    ),
  },
  {
    title: 'ID',
    dataIndex: 'id',
    width: 60,
  },
  {
    title: '来源',
    dataIndex: 'source_platform',
    width: 70,
    render: (platform: string) => {
      const labels: Record<string, string> = { haoyi: '号易', '172': '172', manual: '手动' };
      const colors: Record<string, string> = { haoyi: 'blue', '172': 'orange', manual: 'green' };
      return <Tag color={colors[platform] || 'default'}>{labels[platform] || platform || '-'}</Tag>;
    },
  },
  {
    title: '产品名称',
    dataIndex: 'name',
    width: 180,
    ellipsis: true,
    render: (name) => name || '-',
  },
  {
    title: '运营商',
    dataIndex: 'operator',
    width: 70,
    render: (operator) => {
      const op = operator || '其他';
      const colors: Record<string, string> = {
        移动: 'blue',
        联通: 'green',
        电信: 'orange',
        广电: 'purple',
      };
      return <Tag color={colors[op] || 'default'}>{op}</Tag>;
    },
  },
  {
    title: '月租',
    dataIndex: 'monthly_current',
    width: 70,
    render: (value) => (value ? `${value}元` : '-'),
  },
  {
    title: '通用流量',
    dataIndex: 'traffic_general',
    width: 80,
    render: (value) => value || '-',
  },
  {
    title: '结算价',
    dataIndex: 'settle_price',
    width: 80,
    render: (value) => (value ? `¥${value}` : '-'),
  },
  {
    title: '返佣',
    dataIndex: 'commission_type',
    width: 70,
    render: (value) => value || '-',
  },
  {
    title: '年龄限制',
    dataIndex: 'age_min',
    width: 80,
    render: (_, record) => {
      if (record.age_min != null && record.age_max != null) {
        return `${record.age_min}~${record.age_max}岁`;
      }
      return '-';
    },
  },
  {
    title: '状态',
    dataIndex: 'status',
    width: 70,
    render: (status) => (
      <Tag color={status === '在售' ? 'success' : 'default'}>{status}</Tag>
    ),
  },
  {
    title: '客户端',
    dataIndex: 'is_hidden_from_client',
    width: 90,
    render: (isHidden) => (
      <Tag color={isHidden ? 'red' : 'green'}>{isHidden ? '隐藏' : '可见'}</Tag>
    ),
  },
  {
    title: '重复',
    dataIndex: 'duplicate_status',
    width: 80,
    render: (status = 'none') => (
      <Tag color={duplicateStatusColors[status] || 'default'}>
        {duplicateStatusLabels[status] || status}
      </Tag>
    ),
  },
  {
    title: '库存',
    dataIndex: 'inventory',
    width: 60,
  },
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    width: 120,
    render: (date) => formatDate(date),
  },
  {
    title: '操作',
    key: 'action',
    width: 120,
    render: (_, record) => (
      <Space>
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => onEdit(record)}
        />
        <Dropdown
          menu={{
            items: [
              {
                key: 'detail',
                label: '查看详情',
                icon: <InfoCircleOutlined />,
                onClick: () => onShowDetail(record),
              },
              ...(record.source_platform === '172' ? [{
                key: '172-detail',
                label: '172 HTML详情',
                icon: <InfoCircleOutlined />,
                onClick: () => onFetchPlatform172Detail(record),
              }] : []),
              {
                key: 'status',
                label: record.status === '在售' ? '下架' : '上架',
                onClick: () => onToggleStatus(
                  record.id,
                  record.status === '在售' ? '已下架' : '在售',
                ),
              },
              {
                key: 'pin',
                label: record.is_pinned ? '取消置顶' : '置顶',
                icon: record.is_pinned ? <PushpinFilled /> : <PushpinOutlined />,
                onClick: () => onTogglePin(record.id, record.is_pinned),
              },
              {
                key: 'delete',
                label: '删除',
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => {
                  Modal.confirm({
                    title: '确定要删除这个产品吗？',
                    content: '此操作不可恢复',
                    onOk: () => onDelete(record.id),
                    okText: '确认',
                    cancelText: '取消',
                  });
                },
              },
              ...(record.source_platform === 'haoyi' && record.source_product_id ? [{
                key: 'external-detail',
                label: '号易详情',
                icon: <InfoCircleOutlined />,
                onClick: () => onFetchExternalDetail(record.id),
              }] : []),
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      </Space>
    ),
  },
];
